/**
 * Loads person IB accounts from MobileService `/v1/ib/accounts`.
 * Console/BaaS instance account lists stay in baasAccountsListApi for `/baas` only.
 * List is metadata-only; live saldo/extrato use the active-account proxy.
 */

import intl from "react-intl-universal";
import { fetchAxios } from "../API/fetchAxios";
import { getStoredSelectedAccountId } from "../Constants/accountTypes";
import { useIbProductFeaturesStore } from "../Stores/ibProductFeaturesStore";
import { isIbHomeSelectableAccount } from "../Utils/accountPixEligibility";
import { normalizeIbProductFeatures } from "../Utils/ibProductFeatures";
import { maskTaxIdForList } from "../Utils/masks";

/** Normalize list payloads from ListAccounts (`items`) or profile (`accounts`). */
export const extractAccountItems = (payload) => {
  if (payload == null) return [];

  let data = payload;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return [];
    }
  }

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.accounts)) return data.accounts;
  if (Array.isArray(data?.data?.items)) return data.data.items;
  if (Array.isArray(data?.data?.accounts)) return data.data.accounts;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const VIRTUAL_TYPE_TOKENS = new Set([
  "virtual",
  "otc",
  "wallet",
  "otc_virtual",
  "obr_virtual",
  "segregation_wallet",
]);

const BUSINESS_TYPE_TOKENS = new Set(["business", "pj"]);

const PERSON_TYPE_TOKENS = new Set(["person", "pessoa", "personal", "pf"]);

/**
 * Resolve IB selector badge code: VIRTUAL | BUSINESS | PERSON.
 * Prefer API `account_type` (DisplayAccountType); fall back to role / product tokens.
 */
export const resolveIbAccountDisplayType = (account) => {
  const type = String(account?.account_type || account?.accountType || "")
    .trim()
    .toLowerCase();
  const role = String(account?.role || "")
    .trim()
    .toLowerCase();

  if (VIRTUAL_TYPE_TOKENS.has(type) || VIRTUAL_TYPE_TOKENS.has(role)) {
    return "VIRTUAL";
  }
  if (PERSON_TYPE_TOKENS.has(type) || PERSON_TYPE_TOKENS.has(role)) {
    return "PERSON";
  }
  if (BUSINESS_TYPE_TOKENS.has(type) || BUSINESS_TYPE_TOKENS.has(role)) {
    return "BUSINESS";
  }

  return type ? type.toUpperCase() : "";
};

/** Localized badge label for IB home / transfer account selectors. */
export const formatIbAccountDisplayTypeLabel = (account) => {
  const code = resolveIbAccountDisplayType(account);
  if (!code) return "";

  if (code === "VIRTUAL") {
    return intl.get("ib.accounts.displayType.virtual") || "VIRTUAL";
  }
  if (code === "PERSON") {
    return intl.get("ib.accounts.displayType.person") || "PESSOA";
  }
  if (code === "BUSINESS") {
    return intl.get("ib.accounts.displayType.business") || "BUSINESS";
  }

  return code;
};

/**
 * Secondary selector segment: entity type (`BUSINESS` / `PESSOA`) or `VIRTUAL`.
 * Full option reads `NOME · TIPO` (no ownership/rail chip like NOMINAL).
 */
export const formatIbAccountSecondaryLabel = (account) => {
  const code = resolveIbAccountDisplayType(account);
  if (!code) return "";
  return formatIbAccountDisplayTypeLabel(account);
};

/** Resolve holder document for IB picker rows (ListAccounts /me accounts[]). */
export const resolveIbAccountDocument = (account) => {
  if (!account || typeof account !== "object") return "";
  return String(
    account.document
      || account.DisplayDocument
      || account.display_document
      || account.displayDocument
      || account.national_registration
      || account.nationalRegistration
      || account.raw?.document
      || account.raw?.display_document
      || account.raw?.displayDocument
      || account.raw?.national_registration
      || account.raw?.nationalRegistration
      || "",
  ).trim();
};

/** Account number with digit, e.g. `10023202001-0` (VIRTUAL included when present). */
export const resolveIbAccountNumber = (account) => {
  if (!account) return "";
  const mapped = String(account.accountNumber || "").trim();
  if (mapped && mapped !== "—" && mapped !== "-") return mapped;

  const rawNumber =
    account.account_number
    ?? account.raw?.account_number
    ?? account.banking?.account_number
    ?? account.raw?.banking?.account_number
    ?? "";
  const number = String(rawNumber ?? "").trim();
  // Never invent "-0" from a digit alone when the number is blank (common on VIRTUAL shells).
  if (!number || number === "—" || number === "-") return "";

  const digit =
    account.account_digit
    ?? account.accountDigit
    ?? account.raw?.account_digit
    ?? account.banking?.account_digit
    ?? account.raw?.banking?.account_digit;
  if (digit == null || digit === "") return number;
  return `${number}-${digit}`;
};

/**
 * Agency / branch when the list payload includes it
 * (`branch`, `branch_code`, nested `banking.branch`, …).
 */
export const resolveIbAccountAgency = (account) => {
  if (!account || typeof account !== "object") return "";
  const banking = account.banking || account.raw?.banking || null;
  const raw = String(
    account.agency
      || account.branch
      || account.branch_code
      || account.branchCode
      || account.Agency
      || banking?.branch
      || account.raw?.agency
      || account.raw?.branch
      || account.raw?.branch_code
      || account.raw?.branchCode
      || "",
  ).trim();
  return raw;
};

/** Bank COMPE / institution code when present on list payload. */
export const resolveIbAccountBankCode = (account) => {
  if (!account || typeof account !== "object") return "";
  const banking = account.banking || account.raw?.banking || null;
  return String(
    account.bankCode
      || account.bank_code
      || account.institution_code
      || account.institutionCode
      || banking?.bank_code
      || banking?.institution_code
      || account.raw?.bank_code
      || account.raw?.institution_code
      || "",
  ).trim();
};

/** Optional bank display name (issuer / bank_name). */
export const resolveIbAccountBankName = (account) => {
  if (!account || typeof account !== "object") return "";
  const banking = account.banking || account.raw?.banking || null;
  return String(
    account.bankName
      || account.bank_name
      || account.issuer
      || banking?.bank_name
      || banking?.issuer
      || account.raw?.bank_name
      || account.raw?.issuer
      || "",
  ).trim();
};

/** ISPB / participant code when present on list payload. */
export const resolveIbAccountIspb = (account) => {
  if (!account || typeof account !== "object") return "";
  const banking = account.banking || account.raw?.banking || null;
  return String(
    account.ispb
      || account.ISPB
      || banking?.ispb
      || banking?.ISPB
      || account.raw?.ispb
      || account.raw?.ISPB
      || "",
  ).trim();
};

/** Shell / type tokens that must never render as the entity title. */
const ENTITY_NAME_PLACEHOLDERS = new Set([
  "entity",
  "otc_virtual",
  "obr_virtual",
  "wallet",
  "virtual",
  "business",
  "person",
  "pessoa",
  "pj",
  "pf",
]);

/**
 * Holder / entity display name for IB picker cards.
 * Prefers ListAccounts `name` / `full_name` (DisplayName), then aliases.
 */
export const resolveIbAccountEntityName = (account) => {
  if (!account || typeof account !== "object") return "";
  const raw = account.raw && typeof account.raw === "object" ? account.raw : null;
  const candidates = [
    account.entityName,
    account.entity_name,
    account.holderName,
    account.holder_name,
    account.name,
    account.full_name,
    account.fullName,
    account.alias,
    account.trade_name,
    account.tradeName,
    account.legal_name,
    account.legalName,
    account.person_name,
    account.personName,
    raw?.entity_name,
    raw?.entityName,
    raw?.holder_name,
    raw?.holderName,
    raw?.name,
    raw?.full_name,
    raw?.fullName,
    raw?.alias,
    raw?.trade_name,
    raw?.tradeName,
    raw?.legal_name,
    raw?.legalName,
  ];

  for (const candidate of candidates) {
    const value = String(candidate ?? "").trim();
    if (!value || value === "—" || value === "-") continue;
    if (ENTITY_NAME_PLACEHOLDERS.has(value.toLowerCase())) continue;
    return value;
  }
  return "";
};

/**
 * Primary picker line: account number always (incl. VIRTUAL when known).
 * e.g. `10023202001-0`
 */
export const formatIbAccountPickerPrimaryLine = (account) => {
  if (!account) return "—";
  return resolveIbAccountNumber(account) || "—";
};

/** Entity / holder name line for picker cards (empty when unknown). */
export const formatIbAccountPickerEntityLine = (account) =>
  resolveIbAccountEntityName(account);

/**
 * Bank / agency line: `Banco {code|name} · Ag {agency}` (omit missing parts).
 */
export const formatIbAccountPickerBankLine = (account) => {
  if (!account) return "";
  const bank =
    resolveIbAccountBankCode(account) || resolveIbAccountBankName(account);
  const agency = resolveIbAccountAgency(account);
  const parts = [];
  if (bank) {
    const bankLabel = intl.get("carteira.gate.bankLabel", { bank }) || `Banco ${bank}`;
    parts.push(bankLabel);
  }
  if (agency) {
    const agencyLabel = intl.get("carteira.gate.agencyLabel", { agency }) || `Ag ${agency}`;
    parts.push(agencyLabel);
  }
  return parts.join(" · ");
};

/**
 * Home greeting type eyebrow: Conta Pagamento (PJ) / Conta PF / Conta virtual.
 * Separate from picker badges (BUSINESS / PESSOA / VIRTUAL).
 */
export const formatIbAccountHomeTypeLabel = (account) => {
  const code =
    resolveIbAccountDisplayType(account)
    || String(account?.displayType || "").trim().toUpperCase();
  if (!code) return "";
  if (code === "PERSON") {
    return intl.get("dashboard.homeAccount.typePerson") || "Conta PF";
  }
  if (code === "VIRTUAL") {
    return intl.get("dashboard.homeAccount.typeVirtual") || "Conta virtual";
  }
  if (code === "BUSINESS") {
    return intl.get("dashboard.homeAccount.typePayment") || "Conta Pagamento";
  }
  return formatIbAccountDisplayTypeLabel(account);
};

/**
 * Home entity line: holder / razão social only (document is a separate muted line).
 */
export const formatIbAccountHomeEntityLine = (account) => {
  if (!account) return "";
  return (
    resolveIbAccountEntityName(account)
    || String(account.entityName || account.pickerEntityName || account.label || "").trim()
  );
};

/**
 * Home muted tax-id line: CNPJ/CPF fully formatted (no list mask).
 */
export const formatIbAccountHomeDocumentLine = (account) => {
  if (!account) return "";
  const documentRaw = resolveIbAccountDocument(account);
  if (!documentRaw) return "";

  const digits = String(documentRaw).replace(/\D/g, "");
  if (!digits) return "";

  const kind =
    digits.length === 11
      ? (intl.get("dashboard.homeAccount.taxIdCpf") || "CPF")
      : (intl.get("dashboard.homeAccount.taxIdCnpj") || "CNPJ");

  const formatted =
    digits.length === 11
      ? digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")
      : digits.length === 14
        ? digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5")
        : String(documentRaw).trim();

  if (!formatted || formatted === "—") return "";
  return `${kind} ${formatted}`;
};

/** Home muted line: `ISPB {ispb}` (empty when unknown). */
export const formatIbAccountHomeIspbLine = (account) => {
  const ispb = resolveIbAccountIspb(account);
  if (!ispb) return "";
  return intl.get("carteira.gate.ispbLabel", { ispb }) || `ISPB ${ispb}`;
};

/**
 * Home muted bank name/code: `Banco Valut` or `Banco 697`.
 */
export const formatIbAccountHomeBankNameLine = (account) => {
  if (!account) return "";
  const name = resolveIbAccountBankName(account);
  const code = resolveIbAccountBankCode(account);
  if (name) return `Banco ${name}`;
  if (code) return `Banco ${code}`;
  return "";
};

/**
 * Home dashed-card bank + ISPB line: `Banco 597 - ISPB 34747388` (omit missing).
 */
export const formatIbAccountHomeBankIspbLine = (account) => {
  if (!account) return "";
  const bankLine = formatIbAccountHomeBankNameLine(account);
  const ispbLine = formatIbAccountHomeIspbLine(account);
  if (bankLine && ispbLine) return `${bankLine} - ${ispbLine}`;
  return bankLine || ispbLine;
};

/**
 * Home agency + account on one line: `AG 0001 CC 10023202002-0`.
 */
export const formatIbAccountHomeAgencyAccountDisplay = (account) => {
  if (!account) return "";
  const agency = resolveIbAccountAgency(account);
  const number =
    account.accountNumber
    || resolveIbAccountNumber(account);
  if (!agency && !number) return "";
  if (agency && number) return `AG ${agency} CC ${number}`;
  if (agency) return `AG ${agency}`;
  return `CC ${number}`;
};

/** Clipboard text for agency + account (space-separated). */
export const formatIbAccountHomeAgencyAccountCopyText = (account) => {
  if (!account) return "";
  const agency = resolveIbAccountAgency(account);
  const number =
    account.accountNumber
    || resolveIbAccountNumber(account);
  if (!agency && !number) return "";
  if (agency && number) return `${agency} ${number}`;
  return agency || number;
};

/**
 * Clipboard text for the home payment-details card (full banking block).
 * Prefer multiline CNPJ / Banco-ISPB / AG·CC when available.
 */
export const formatIbAccountHomeBankDetailsCopyText = (account) => {
  if (!account) return "";
  const lines = [
    formatIbAccountHomeDocumentLine(account),
    formatIbAccountHomeBankIspbLine(account),
    formatIbAccountHomeAgencyAccountDisplay(account),
  ].filter(Boolean);
  return lines.join("\n");
};

/**
 * Home muted agency line: `Ag 0001`.
 */
export const formatIbAccountHomeAgencyLine = (account) => {
  if (!account) return "";
  const agency = resolveIbAccountAgency(account);
  if (!agency) return "";
  return intl.get("carteira.gate.agencyLabel", { agency }) || `Ag ${agency}`;
};

/**
 * Home muted account line: `Conta 600241-6` (same for VIRTUAL when number exists).
 */
export const formatIbAccountHomeAccountLine = (account) => {
  if (!account) return "";
  const number =
    account.accountNumber
    || resolveIbAccountNumber(account);
  if (!number || number === "—" || number === "-") return "";
  return `Conta ${number}`;
};

/**
 * Home muted bank details: `Ag 0001 · Conta 600241-6` (omit missing).
 */
export const formatIbAccountHomeBankLine = (account) => {
  if (!account) return "";
  const parts = [
    formatIbAccountHomeAgencyLine(account),
    formatIbAccountHomeAccountLine(account),
  ].filter(Boolean);
  return parts.join(" · ");
};

/**
 * @deprecated Prefer formatIbAccountHomeIspbLine + formatIbAccountHomeBankLine.
 * Kept for callers that still expect a single muted meta string.
 */
export const formatIbAccountHomeMetaLine = (account) => {
  if (!account) return "";
  const lines = [
    formatIbAccountHomeIspbLine(account),
    formatIbAccountHomeBankLine(account),
  ].filter(Boolean);
  return lines.join(" · ");
};

/**
 * Type (+ masked document) line: `PESSOA · 303.***.***-08` or just `VIRTUAL`.
 */
export const formatIbAccountPickerSecondaryLine = (account) => {
  if (!account) return "";
  const type =
    account.secondaryLabel
    || formatIbAccountSecondaryLabel(account)
    || formatIbAccountDisplayTypeLabel(account)
    || "";
  const documentRaw = resolveIbAccountDocument(account);
  const document = documentRaw ? maskTaxIdForList(documentRaw) : "";
  if (type && document && document !== "—") return `${type} · ${document}`;
  if (type) return type;
  if (document && document !== "—") return document;
  return "";
};

/**
 * Legacy flat label: DOCUMENTO | TIPO | NUMERO
 * e.g. `303.***.***-08 | PESSOA | 10023202001-0`
 */
export const formatIbAccountPickerLabel = (account) => {
  if (!account) return "—";
  const document = maskTaxIdForList(resolveIbAccountDocument(account));
  const type =
    account.secondaryLabel
    || formatIbAccountSecondaryLabel(account)
    || formatIbAccountDisplayTypeLabel(account)
    || "—";
  const number = resolveIbAccountNumber(account) || "—";
  return `${document} | ${type} | ${number}`;
};

export const mapOperatingAccountOption = (account) => {
  const id = String(account?.id || account?.account_id || "").trim();
  const number = resolveIbAccountNumber(account);
  const agency = resolveIbAccountAgency(account);
  const bankCode = resolveIbAccountBankCode(account);
  const bankName = resolveIbAccountBankName(account);
  const ispb = resolveIbAccountIspb(account);
  const role = String(account?.role || "").trim();
  const displayType = resolveIbAccountDisplayType(account);
  const displayTypeLabel = formatIbAccountDisplayTypeLabel(account);
  const entityName = resolveIbAccountEntityName(account);
  const name = entityName || displayTypeLabel || id;
  const document = resolveIbAccountDocument(account);

  // List endpoint is metadata-only (no per-account balances). Live saldo comes from
  // GET /v1/ib/accounts/balances for the active account after select/activate.
  const features = normalizeIbProductFeatures(account?.features);

  const option = {
    id,
    label: name,
    entityName: entityName || null,
    secondaryLabel: formatIbAccountSecondaryLabel(account),
    balanceHint: null,
    accountNumber: number,
    agency: agency || null,
    bankCode: bankCode || null,
    bankName: bankName || null,
    ispb: ispb || null,
    document,
    role,
    displayType,
    entityId: String(account?.entity_id || account?.entityId || "").trim() || null,
    isVirtual:
      displayType === "VIRTUAL"
      || account.is_virtual === true
      || account.isVirtual === true,
    canPix:
      account.can_pix !== false
      && account.canPix !== false
      && features.pix !== false,
    features,
    balances: [],
    lifecycle: String(account?.lifecycle || account?.account_lifecycle || "").trim().toLowerCase() || null,
    active: account?.active !== false,
    closedAccount: account?.closed_account === true || account?.closedAccount === true,
    accountStatusId: account?.account_status_id ?? account?.accountStatusId ?? null,
    supportStatus: account?.support_status || account?.supportStatus || null,
    raw: account,
  };
  option.pickerEntityName = entityName;
  option.pickerPrimary = formatIbAccountPickerPrimaryLine(option);
  option.pickerBankLine = formatIbAccountPickerBankLine(option);
  option.pickerSecondary = formatIbAccountPickerSecondaryLine(option);
  option.pickerLabel = formatIbAccountPickerLabel(option);
  option.homeTypeLabel = formatIbAccountHomeTypeLabel(option);
  option.homeEntityLine = formatIbAccountHomeEntityLine(option);
  option.homeDocumentLine = formatIbAccountHomeDocumentLine(option);
  option.homeIspbLine = formatIbAccountHomeIspbLine(option);
  option.homeBankNameLine = formatIbAccountHomeBankNameLine(option);
  option.homeBankIspbLine = formatIbAccountHomeBankIspbLine(option);
  option.homeAgencyLine = formatIbAccountHomeAgencyLine(option);
  option.homeAccountLine = formatIbAccountHomeAccountLine(option);
  option.homeAgencyAccountLine = formatIbAccountHomeAgencyAccountDisplay(option);
  option.homeAgencyAccountCopyText = formatIbAccountHomeAgencyAccountCopyText(option);
  option.homeBankDetailsCopyText = formatIbAccountHomeBankDetailsCopyText(option);
  option.homeBankLine = formatIbAccountHomeBankLine(option);
  option.homeMetaLine = formatIbAccountHomeMetaLine(option);
  return option;
};

/** Sync Carteira menu product gates from the active / selected IB account. */
export const syncIbProductFeaturesFromAccounts = (accounts) => {
  const list = Array.isArray(accounts) ? accounts : [];
  if (!list.length) return;

  const stored = getStoredSelectedAccountId();
  const selected =
    (stored != null && stored !== ""
      ? list.find((account) => String(account.id) === String(stored))
      : null)
    || list.find((account) => account.raw?.set_app_active)
    || list[0];

  if (selected?.features) {
    useIbProductFeaturesStore.getState().setFeatures(selected.features);
  }
};

/**
 * Person-scoped IB accounts (MobileService). Ignores Console instance argument.
 * @returns {Promise<Array<{ id, label, secondaryLabel, balanceHint, ... }>>}
 */
export const fetchOperatingAccounts = async (_instance = null) => {
  void _instance;
  const response = await fetchAxios({
    verb: "GET",
    context: "ibAccounts",
    responseType: "json",
    cancelToken: undefined,
  });
  const items = extractAccountItems(response?.data);
  const mapped = items
    .map(mapOperatingAccountOption)
    .filter((account) => account.id && isIbHomeSelectableAccount(account));
  syncIbProductFeaturesFromAccounts(mapped);
  return mapped;
};

export { dedupeAccountsByStableId, getAccountStableId } from "./baasAccountsListApi";
